import { useCallback, useEffect, useMemo, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'

import AppUsage from './AppUsage'
import Builder from './Builder'
import Community from './Community'
import CustomDashboard from './CustomDashboard'
import MonetAds from './MonetAds'
import MonetIap from './MonetIap'
import Overview from './Overview'
import OverviewMetrics from './OverviewMetrics'
import PublishingOverview from './PublishingOverview'
import Sandbox from './Sandbox'
import Stability from './Stability'
import Usage from './Usage'

import SwitchButton from '~/components/Buttons/SwitchButton'
import ViewTitle from '~/components/ViewTitle'
import { DEFAULT_DAI_MULTIPLE_VALUES, DEFAULT_DAI_SINGLE_VALUE, AUTHORIZATIONS } from '~/const'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useAppEventsListQuery } from '~/queries/events'
import { isBusinessReportsFeature } from '~/utils/analytics-context'
import { getHeaderParams } from '~/utils/application'

import './styles.scss'

const X_AXIS_OPTIONS = {
  DAI: 'dai',
  INSTALLATION_DATE: 'installation_date',
}

export const XAxisSwitchButton = ({
  className,
  onSelectionChange,
}: {
  className?: string
  onSelectionChange: (v: string) => void
}): ReactElement => {
  /** Local state */

  const { dispatch, state } = useAnalyticsContext()

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([
    {
      content: 'Installation date',
      id: X_AXIS_OPTIONS.INSTALLATION_DATE,
      isActive: state.filters.kpi?.[0] === 'installation_date',
    },
    {
      content: 'Day After Install',
      id: X_AXIS_OPTIONS.DAI,
      isActive: state.filters.kpi?.[0] === 'dai',
    },
  ])

  const isBusinessReports = useMemo(() => isBusinessReportsFeature(state.feature), [state.feature])

  /** Effects */

  useEffect(() => {
    if (state.filters.kpi?.[0]) {
      setSwitchButtons((prevState) =>
        prevState.map((button) => ({
          ...button,
          isActive: button.id === state.filters.kpi?.[0],
        }))
      )
      onSelectionChange(state.filters.kpi?.[0])
    }
  }, [onSelectionChange, state.filters.kpi])

  /** Handlers */

  const handleSwitchButtonSelectionChange = useCallback(
    (selectedKpi: string) => {
      if (state.filters.kpi?.[0] === selectedKpi) return

      const newFilters = {
        dai:
          selectedKpi === 'dai' || state.filters.breakdown?.[0] === 'dai'
            ? DEFAULT_DAI_MULTIPLE_VALUES
            : DEFAULT_DAI_SINGLE_VALUE,
        kpi: [selectedKpi],
      }

      dispatch({ payload: newFilters, type: 'SET_FILTERS' })

      dispatchToSearchParams({ type: 'update', value: newFilters })

      onSelectionChange(selectedKpi)
    },
    [dispatch, dispatchToSearchParams, onSelectionChange, state.filters.breakdown, state.filters.kpi]
  )

  /** Effects */

  useEffect(() => {
    isBusinessReports && handleSwitchButtonSelectionChange('installation_date')
  }, [isBusinessReports, handleSwitchButtonSelectionChange])

  /** Render */

  return (
    <SwitchButton
      buttons={switchButtons}
      className={className}
      disabled={isBusinessReports}
      onButtonSelectionChange={handleSwitchButtonSelectionChange}
    />
  )
}

interface Props {
  withEvents: boolean
}

const CohortedAnalyticsRouter = ({ withEvents }: Props): ReactElement => {
  /** Local state */

  const { pathname } = useLocation()

  const graphConfigAuthorized = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.READ])

  const titleParams = useMemo(() => getHeaderParams(pathname), [pathname])

  const [xAxisParam, setXAxisParam] = useState(X_AXIS_OPTIONS.INSTALLATION_DATE)

  /** Queries & Mutations */

  const { appDetails, appKey, deviceType, isLoading } = useAppSummary()

  const { data: eventsData } = useAppEventsListQuery({
    appKey,
    deviceType,
    enabled: xAxisParam !== X_AXIS_OPTIONS.DAI,
  })

  /** Query dependent state */

  const viewProps = useMemo(
    () => ({
      eventsData: withEvents ? eventsData : undefined,
      isAppLoading: isLoading,
      isAppOwned: true,
      kpiField: xAxisParam,
    }),
    [eventsData, isLoading, withEvents, xAxisParam]
  )

  /** Handlers */

  const handleSelectionChange = (value: string) => setXAxisParam(value)

  /** Render */

  return (
    <div className="view-layout cohorted-analytics-router">
      <ViewTitle className="layout-view-title">
        {titleParams?.title}
        {!pathname.includes('metrics-overview') ? (
          <XAxisSwitchButton onSelectionChange={handleSelectionChange} />
        ) : null}
      </ViewTitle>
      {appDetails ? (
        <Routes>
          <Route element={<AppUsage {...viewProps} />} path="app-usage" />
          <Route element={<Community {...viewProps} />} path="community" />
          <Route element={<CustomDashboard {...viewProps} />} path="favourites" />
          <Route element={<Stability {...viewProps} />} path="stability" />
          <Route element={<OverviewMetrics {...viewProps} />} path="metrics-overview" />
          <Route element={<MonetAds {...viewProps} />} path="monet-ads" />
          <Route element={<MonetIap {...viewProps} />} path="monet-iap" />
          <Route element={<Overview {...viewProps} />} path="overview" />
          <Route
            element={
              graphConfigAuthorized ? <PublishingOverview {...viewProps} /> : <Navigate replace to="/unauthorized" />
            }
            path="publishing-overview"
          />
          <Route
            element={graphConfigAuthorized ? <Sandbox {...viewProps} /> : <Navigate replace to="/unauthorized" />}
            path="sandbox"
          />
          <Route element={<Usage {...viewProps} />} path="usage" />
          <Route
            element={graphConfigAuthorized ? <Builder {...viewProps} /> : <Navigate replace to="/unauthorized" />}
            path="builder/:id?"
          />
          <Route element={<Navigate replace to="/404" />} path="*" />
        </Routes>
      ) : null}
    </div>
  )
}

export default CohortedAnalyticsRouter
